import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { typeColor } from '../lib/format.js';
import KopierKnopf from '../components/KopierKnopf.jsx';

/**
 * Trainerkämpfe, allgemein — bewusst ohne Konto.
 *
 * Die Liga-Hinweise stehen gleichwertig neben der Pokémon-Liste: bei
 * Trainerkämpfen entscheidet oft weniger, WELCHES Pokémon man nimmt, als
 * unter welchen Bedingungen. Ein perfektes 15/15/15 ist in der Superliga
 * schlechter als ein 0/15/15 derselben Art.
 */
export default function PvpPage() {
  const [daten, setDaten] = useState(null);
  const [liga, setLiga] = useState('great');
  const [fehler, setFehler] = useState(null);

  useEffect(() => {
    api.pvp().then(setDaten).catch((e) => setFehler(e.message));
  }, []);

  if (fehler) return <div className="error">{fehler}</div>;
  if (!daten) return <div className="skeleton" style={{ height: 240 }} />;

  const aktuell = daten.leagues.find((l) => l.key === liga) ?? daten.leagues[0];

  return (
    <>
      <section className="hero">
        <span className="hero__tag">Trainerkämpfe</span>
        <h1>Was zählt in den Ligen?</h1>
        <p className="hero__sub">Bewertung je Liga, unabhängig von der Raid-Stärke.</p>
      </section>

      <div className="panel" style={{ marginBottom: 32 }}>
        <h2 style={{ marginBottom: 12 }}>Gilt in allen Ligen</h2>
        <ul className="bonus-list">
          {daten.basics.map((b) => <li key={b}>{b}</li>)}
        </ul>
      </div>

      <div className="filters">
        {daten.leagues.map((l) => (
          <button
            key={l.key}
            type="button"
            className={liga === l.key ? 'chip chip--on' : 'chip'}
            onClick={() => setLiga(l.key)}
          >
            {l.name} · {l.cap}
          </button>
        ))}
      </div>

      <div className="panel" style={{ marginBottom: 24 }}>
        <h2 style={{ marginBottom: 6 }}>{aktuell.name}</h2>
        <p className="gruppe__text" style={{ marginBottom: 12 }}>{aktuell.kurz}</p>
        <ul className="bonus-list">
          {aktuell.punkte.map((p) => <li key={p}>{p}</li>)}
        </ul>
      </div>

      {daten.source && (
        <p className="quelle">
          Ranglisten von{' '}
          <a href={daten.source.url} target="_blank" rel="noreferrer noopener">
            {daten.source.name}
          </a>
        </p>
      )}

      {aktuell.team?.members?.length > 0 && (
        <div className="team">
          <div className="team__kopf">
            <div>
              <strong>Dreierteam</strong>
              <div className="card__meta">
                {aktuell.team.shared.length === 0
                  ? 'Keine gemeinsame Schwäche.'
                  : 'Gemeinsam anfällig gegen ' + aktuell.team.shared.join(', ') + '.'}
              </div>
            </div>
            <KopierKnopf text={aktuell.search} label="Team kopieren" title={aktuell.search} />
          </div>

          <div className="team__reihe">
            {aktuell.team.members.map((m) => (
              <div className="team__mitglied" key={m.name}>
                <span className="team__name">{m.name}</span>
                <span className="inline">
                  {m.types.map((t) => (
                    <span className="type" key={t} style={{ '--type-color': typeColor(t) }}>{t}</span>
                  ))}
                </span>
                <span className="team__schwach">
                  schwach gegen {m.schwaechen.join(', ') || '—'}
                </span>
              </div>
            ))}
          </div>

          {aktuell.searchAll && aktuell.searchCount > 3 && (
            <div className="team__alle">
              <span className="text-faint">
                Alle {aktuell.searchCount} Kandidaten dieser Liga
              </span>
              <KopierKnopf text={aktuell.searchAll} label="Liste kopieren" title={aktuell.searchAll} />
            </div>
          )}
        </div>
      )}

      {aktuell.pokemon.length === 0 ? (
        <div className="empty">Für diese Liga ist noch nichts bewertet.</div>
      ) : (
        <div className="grid">
          {aktuell.pokemon.map((p) => <PvpKarte key={p.slug} mon={p} />)}
        </div>
      )}

    </>
  );
}

function PvpKarte({ mon }) {
  return (
    <Link to={'/pokemon/' + mon.slug} className="card mon-card">
      <div className="card__top">
        <div>
          <div className="card__title">
            {mon.name}
            {mon.form && <span className="mon-card__form"> {mon.form}</span>}
          </div>
          <div className="mon__dex">#{String(mon.dexNo).padStart(3, '0')}</div>
        </div>
        <div className="pvp-karte__wertung">
          <span className={'tier tier--' + mon.tier}>{mon.tier}</span>
          {mon.score !== null && <span className="pvp-karte__score">{mon.score}</span>}
        </div>
      </div>

      <div className="inline">
        {mon.types.map((t) => (
          <span className="type" key={t} style={{ '--type-color': typeColor(t) }}>{t}</span>
        ))}
        {mon.rankNo && <span className="badge badge--soft">Platz {mon.rankNo}</span>}
      </div>

      {mon.keyMoves && mon.keyMoves !== '-' && (
        <div className="pvp-werte">
          <span className="pvp-werte__label">Attacken</span>
          <span className="pvp-werte__wert">{mon.keyMoves}</span>
        </div>
      )}

      {mon.notes && <p className="card__summary">{mon.notes}</p>}
    </Link>
  );
}
